(ns manage.content-variant-page.table.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Skeleton$default" :as Skeleton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GRID_CHECKBOX_SELECTION_COL_DEF GridActionsCellItem GridColumnMenu GridToolbarColumnsButton GridToolbarDensitySelector GridToolbarFilterButton]]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.debouncer :as debouncer]
            [common.flags :refer [use-flag]]
            [common.localstorage :as localstorage]
            [common.mui :as mui]
            [common.util :as util]
            [common.validators.core :refer [url?]]
            [common.widgets.column-renderers :refer [template-dimensions-renderer]]
            [common.widgets.data-grid :as dg]
            [common.widgets.progress :refer [linear-progress-with-label]]
            [manage.ad-variant.widgets :as variant-widgets]
            [manage.adset.hooks :refer [use-channel]]
            [manage.content-variant-page.table.db :as table.db]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defui preview-asset [{:keys [type value]}]
  {:pre [(url? value)]}
  (case type
    "audio" ($ :audio {:controls true
                       :src value
                       :style {:maxHeight "100%"}})
    "image" ($ :img {:src value
                     :on-click #(js/window.open value "_blank")
                     :style {:maxWidth "100%"
                             :maxHeight "80%"
                             :cursor "pointer"}})
    "video" ($ :div {:style {:position "relative"
                             :maxWidth "100%"
                             :maxHeight "4rem"}}
               ($ Icon {:style {:position "absolute"
                                :top "50%"
                                :left "2px"
                                :transform "translateY(-50%)"}}
                  "ondemand_video")
               ($ :video {:src value
                          :on-click #(js/window.open value "_blank")
                          :style {:maxHeight "4rem"
                                  :maxWidth "100%"}}))
    ($ Link
       {:color "inherit"
        :underline "hover"
        :no-wrap true
        :href value :target "_blank"}
       value)))

(def render-status->content
  {:unknown {:description table.db/render-none-str
             :tooltip-description "No render is present"
             :icon "help_outline"}
   :failed {:description table.db/render-failed-str
            :tooltip-description "The render failed"
            :icon "info"
            :iconProps {:sx #js {".MuiButtonBase-root &" #js {:color "error.main"}}}}
   :succeeded {:description table.db/render-succeeded-str
               :tooltip-description "The render has successfully completed"
               :icon "done"}
   :queued {:description table.db/render-queued-str
            :tooltip-description "The render is queued and ready to start as soon as slots become available"
            :icon "auto_awesome_motion"}
   :render-starting {:description table.db/render-starting-str
                     :tooltip-description "The rendering process has started, machines start spinning up to take on renders"
                     :icon "not_started"}
   :rendering {:description table.db/render-rendering-str
               :tooltip-description "The video is being rendered at this moment"
               :icon "flip"}})

(defui header [{:keys [field children]}]
  ($ Stack {:gap 0.5 :sx #js {:lineHeight 1}}
     ($ Typography
        {:fontWeight 500
         :lineHeight "inherit"}
        field)
     ($ Box
        {:fontSize "12px"
         :color "grey.30"}
        children)))

(defn identifier-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "identifier-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 200
                     :flex 1
                     :valueGetter (fn [value row ^js column ^js apiRef]
                                    (:content-id row))
                     :renderCell (fn [^js params]
                                   (let [value (.-value params)
                                         default? (:default-variant (.-row params))]
                                     ($ Stack {:direction "row"
                                               :gap 1
                                               :sx #js {:flexGrow 1
                                                        :justifyContent "space-between"
                                                        :minWidth "80px"}}
                                        ($ Typography {:noWrap true} value)
                                        (when default?
                                          ($ Chip {:label "D"
                                                   :sx #js {:fontWeight "600"
                                                            :borderRadius 100}})))))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "ID"))))))))

(defn row-number-column []
  [{:field "sequence-number"
    :headerName "#"
    :valueGetter (fn [value row ^js column ^js apiRef]
                   (:sequence-number row))
    :minWidth 50
    :width 50}])

(defn last-modified-column []
  [{:field "updated-at"
    :headerName "Last modified"
    :width 150
    :filterable false
    :valueGetter (fn [value row ^js column ^js apiRef]
                   (:updated-at row))
    :renderCell (fn [^js params]
                  ($ Tooltip
                     {:placement :right
                      :title (util/format-iso-string (->clj (.-value params)))}
                     ($ :span (util/human-readable-relative-time (->clj (.-value params))))))}])

(defn content-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "content-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 140
                     :flex 1
                     :filterable (if (contains? #{"video" "image" "audio"} (get-in column [:placeholder :type]))
                                   false
                                   true)
                     :valueGetter (fn [_value row ^js _column ^js apiRef]
                                    (let [value (->clj (get-in row [:values (keyword (:name column))]))]
                                      (if (sequential? value)
                                        (str/join ", " value)
                                        value)))
                     :renderCell (fn [^js params]
                                   (let [value (or (.-value params) "")
                                         placeholder (.. params -colDef -placeholder)]
                                     (if (and (url? value)
                                              (some? placeholder))
                                       ($ preview-asset {:type (.-type placeholder) :value value})
                                       value)))
                     :renderHeader (fn [^js params]
                                     (let [placeholder (.. params -colDef -placeholder)]
                                       ($ header {:field (.-field params)}
                                          ($ :span "Content" (when (some? placeholder)
                                                               (str ": " (.-type placeholder))))))))))))

(defn targeting-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "targeting-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 140
                     :flex 1
                     :valueGetter (fn [_value row ^js _column ^js apiRef]
                                    (let [value (->clj (get-in row [:values (keyword (:name column))]))]
                                      (if (sequential? value)
                                        (str/join ", " value)
                                        value)))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Targeting"))))))))

(defn- optimizing-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "optimizing-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 140
                     :flex 1
                     :valueGetter (fn [value row ^js _column ^js apiRef]
                                    (get-in row [:values (keyword (:name column))]))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Optimizing"))))))))

(defui url-cell-renderer [{:keys [value]}]
  (when value
    ($ Tooltip {:placement :right
                :title value}
       ($ Link
          {:color "inherit"
           :underline "hover"
           :noWrap true
           :href value
           :target "_blank"
           :rel "noreferrer noopener"}
          value))))

(defn click-through-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "click-through-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 240
                     :flex 1
                     :valueGetter (fn [value row ^js _column ^js apiRef]
                                    (get-in row [:values (keyword (:name column))]))
                     :renderCell (fn [^js params]
                                   ($ url-cell-renderer {:value (.-value params)}))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Click-through"))))))))

(defn impression-tracker-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "third-party-impression-tracker-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 240
                     :flex 1
                     :valueGetter (fn [value row ^js _column ^js apiRef]
                                    (get-in row [:values (keyword (:name column))]))
                     :renderCell (fn [^js params]
                                   ($ url-cell-renderer {:value (.-value params)}))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Impression Tracker"))))))))

(defn click-tracker-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "third-party-click-tracker-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 240
                     :flex 1
                     :valueGetter (fn [value row ^js _column ^js apiRef]
                                    (get-in row [:values (keyword (:name column))]))
                     :renderCell (fn [^js params]
                                   ($ url-cell-renderer {:value (.-value params)}))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Click Tracker"))))))))

(defn template-dimensions-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "template-dimensions-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 240
                     :flex 1
                     :sortable false
                     :filterable false
                     :valueGetter (fn [value row ^js column ^js apiRef]
                                    (:template-dimensions row))
                     :renderCell (fn [^js params]
                                   (let [{adset :value} (use-subscribe [:resource.adset/one])
                                         supported-dimensions (->> adset
                                                                   :templateRevisions
                                                                   (map #(select-keys % [:width :height]))
                                                                   set)]
                                     ($ template-dimensions-renderer
                                        {:templates-dimensions (->clj (.-value params))
                                         :width (.. params -colDef -computedWidth)
                                         :supported-dimensions supported-dimensions})))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "Dimensions"))))))))

(defn censor-columns []
  (->> @(rf/subscribe [:table.content-variants/columns-by-class "clear-cast-id-key"])
       (map (fn [column]
              (assoc column
                     :field (:name column)
                     :minWidth 240
                     :flex 1
                     :valueGetter (fn [value row ^js _column ^js apiRef]
                                    (get-in row [:censors :clear-cast-id]))
                     :renderCell (fn [^js params]
                                   ($ url-cell-renderer {:value (.-value params)}))
                     :renderHeader (fn [^js params]
                                     ($ header {:field (.-field params)}
                                        ($ :span "ClearCast ID"))))))))

(defhook use-status-columns []
  (let [adset-channel (use-channel)
        score-column-is-shown (use-flag :content-variant-score-column)
        overall-progress (use-subscribe [:table.content-variants/renders-overall-progress])
        [n-items status] (use-subscribe [:table.content-variants/renders-overall-status])]
    (cond-> []
      (not= adset-channel "display") (conj {:field "renders"
                                            :headerName "Renders"
                                            :class "render-status" ; For filtering
                                            :type "singleSelect"
                                            :width 140
                                            :align "center"
                                            :headerAlign "center"
                                            :valueOptions (clj->js [table.db/render-queued-str
                                                                    table.db/render-starting-str
                                                                    table.db/render-rendering-str
                                                                    table.db/render-succeeded-str
                                                                    table.db/render-failed-str
                                                                    table.db/render-none-str])
                                            :valueGetter (fn [value row ^js column ^js apiRef]
                                                           (let [row (->clj row)]
                                                             (table.db/derive-render-status row)))
                                            :renderCell (fn [^js params]
                                                          (let [render-status (.-value params)
                                                                content (render-status->content render-status)]
                                                            ($ Tooltip
                                                               {:placement :left
                                                                :title (:tooltip-description content)}
                                                               ($ Button
                                                                  {:variant "text"
                                                                   :sx #js {:color "inherit"}
                                                                   :startIcon ($ Icon (:iconProps content)
                                                                                 (:icon content))
                                                                   :onClick #(rf/dispatch [:modal.content-variants/open-renders-modal
                                                                                           {:variant-ids [(.. params -row -id)]
                                                                                            :action :info
                                                                                            :status render-status}])}
                                                                  (:description content)))))
                                            :renderHeader (fn [^js params]
                                                            ($ header {:field (.. params -colDef -headerName)}
                                                               (cond
                                                                 (not= overall-progress 100)
                                                                 ($ linear-progress-with-label
                                                                    {:value overall-progress
                                                                     :sx #js {:width "8rem"}})

                                                                 (= status :failed)
                                                                 ($ Stack {:direction "row" :alignItems "center" :gap 1}
                                                                    ($ Icon {:color "error"
                                                                             :fontSize "small"} "info")
                                                                    ($ :span (str n-items " failed")))

                                                                 (= status :succeeded)
                                                                 ($ Stack {:direction "row" :alignItems "center" :gap 1}
                                                                    ($ Icon {:fontSize "small"} "done")
                                                                    ($ :span "All")))))})

      true (conj {:field "status"
                  :headerName "Status"
                  :class "variant-status" ; For filtering
                  :type "singleSelect"
                  :width 100
                  :align "center"
                  :headerAlign "center"
                  :valueOptions (clj->js [:draft :approved :published :publishing :unpublished :unpublishing])
                  :valueGetter (fn [value row ^js column ^js apiRef]
                                 (let [row (->clj row)]
                                   (:status row)))
                  :renderCell (fn [^js params]
                                ($ Stack {:direction "row" :alignItems "center" :gap 1}
                                   ($ Chip {:label (.-value params)
                                            :icon (when (#{"publishing" "unpublishing"} (.-value params))
                                                    ($ CircularProgress {:size 10}))})))})

      score-column-is-shown (conj {:field "score"
                                   :headerName "Predictive score"
                                   :filterable false
                                   :width 100
                                   :align "center"
                                   :headerAlign "center"
                                   :valueGetter (fn [_value row ^js _column ^js _apiRef]
                                                  (let [row (->clj row)
                                                        createlift-first-asset (-> row
                                                                                   :renders
                                                                                   first
                                                                                   :exports
                                                                                   :createlift
                                                                                   :assets
                                                                                   first)]
                                                    (when (= (:status createlift-first-asset) "done")
                                                      (-> createlift-first-asset
                                                          :data
                                                          :score))))
                                   :renderCell (fn [^js params]
                                                 ($ variant-widgets/predictive-score-chip {:score (.-value params)}))}))))

(defn action-column []
  (let [adset-channel (use-subscribe [:resource.adset/current-channel])]
    [{:field "actions"
      :headerName "Actions"
      :type "actions"
      :width 80
      :getActions (fn [^js params]
                    (let [row (.-row params)
                          status (table.db/derive-variant-status row)
                          render-status (table.db/derive-render-status row)
                          update-status (fn [status]
                                          (rf/dispatch [:table.content-variants/update-variant-statuses status [(:id row)]]))]
                      (->> (cond-> []
                             (not= adset-channel "display") (conj {:icon ($ Icon "download")
                                                                   :label "Download renders"
                                                                   :onClick #(rf/dispatch [:table.content-variants/download-variant-renders (:id row)])})

                             (= status :draft) (conj {:icon ($ Icon "check")
                                                      :label "Mark as approved"
                                                      :onClick #(update-status :approve)})

                             (or (= status :approved)
                                 (= status :unpublished)) (conj {:icon ($ Icon "close")
                                                                 :label "Mark as unapproved"
                                                                 :onClick #(update-status :unapprove)})

                             (not= status :published) (conj {:icon ($ Icon "publish")
                                                             :label "Publish"
                                                             :disabled (= status :draft)
                                                             :onClick #(update-status :publish)})

                             (and (= status :published)
                                  (not (:default-variant row))) (concat [{:icon ($ Icon {:sx #js {:transform "rotate(180deg)"}} "file_upload")
                                                                          :label "Unpublish"
                                                                          :onClick #(update-status :unpublish)}
                                                                         {:icon ($ Icon "label")
                                                                          :label "Mark as default variant"
                                                                          :onClick #(update-status :default)}])

                             (not= adset-channel "display") (concat [{:icon ($ Icon "label")
                                                                      :label "Trigger Render"
                                                                      :onClick #(rf/dispatch [:resource.content-variants/trigger-render-many [(:id row)] :trigger])
                                                                      :disabled (#{:rendering :render-starting :queued :succeeded} render-status)}
                                                                     {:icon ($ Icon "close")
                                                                      :label "Cancel Render"
                                                                      :onClick #(rf/dispatch [:resource.content-variants/trigger-render-many [(:id row)] :cancel])
                                                                      :disabled (#{:failed :created :unknown :succeeded} render-status)}])

                             true (concat [{:icon ($ Icon "devices_other")
                                            :label "Preview"
                                            :onClick #(rf/dispatch [:table.content-variants/preview {:cursor (:cursor row) :variant-id (:id row)}])}]))

                           (mapv #($ GridActionsCellItem (assoc % :showInMenu true)))
                           clj->js)))}]))

(defui targeting-menu-item [{:keys [^js colDef onClick] :as props}]
  ($ MenuItem
     (merge props
            {:onClick (fn [e]
                        (onClick e)
                        (rf/dispatch [:entity/view {:entity :placeholder :name (.-field colDef)}]))})
     "Configure targeting"))

(def ColumnMenu
  (uix/as-react
   (fn [{:keys [^js colDef] :as props}]
     (let [targeting-column? (= (.-class colDef) "targeting-key")]
       ($ GridColumnMenu
          (cond-> (->clj props)
            targeting-column?
            (merge {:slots #js {:targetingMenuItem (uix/as-react
                                                    (fn [props]
                                                      ($ targeting-menu-item props)))}
                    :slotProps #js {:targetingMenuItem #js {:displayOrder 1}}})))))))

(defui content-columns-toggle [{:keys [show-content-columns set-show-content-columns]}]
  ($ FormControl
     ($ FormControlLabel
        {:label "Show content"
         :control ($ Switch
                     {:checked show-content-columns
                      :onChange (fn [e]
                                  (set-show-content-columns (.. e -target -checked)))
                      :sx #js {:mx 1}})})))

(defui bulk-actions [{:keys [actions]}]
  (let [update-bulk #(rf/dispatch [:table.content-variants/update-variant-statuses-bulk %])
        action-mapping {:publish {:label "Publish all"
                                  :icon "publish"
                                  :order 25
                                  :on-click (partial update-bulk :publish-bulk)}
                        :unpublish {:label "Unpublish all"
                                    :icon "file_upload"
                                    :iconProps {:sx #js {:transform "rotate(180deg)"}}
                                    :order 30
                                    :on-click (partial update-bulk :unpublish-bulk)}
                        :approve {:label "Approve all"
                                  :icon "check"
                                  :order 10
                                  :on-click (partial update-bulk :approve-bulk)}
                        :unapprove {:label "Unapprove all"
                                    :icon "close"
                                    :order 15
                                    :on-click (partial update-bulk :unapprove-bulk)}}
        actions* (->> (map (fn [[action count]]
                             (-> (get action-mapping action)
                                 (assoc :count count))) actions)
                      (sort-by :order))]
    ($ Stack {:direction "row"}
       (for [[index {:keys [label on-click iconProps count] icon-name :icon}] (mc/seq-indexed actions*)]
         ($ Button
            {:key index
             :variant "text"
             :size "small"
             :startIcon ($ Icon iconProps icon-name)
             :onClick on-click
             :data-testid label}
            (str label " (" count ")"))))))

(def Toolbar
  (uix/as-react
   (fn [{:keys [show-content-columns set-show-content-columns]}]
     (let [[filter-model _] (dg/use-filter-model)
           adset (use-subscribe [:resource.adset/one])
           channel (-> adset :value :channel)
           adset-id (get-in (use-subscribe [:resource.adset/one]) [:value :id])
           update-status #(rf/dispatch [:table.content-variants/update-selected-variant-statuses %])
           trigger-renders #(rf/dispatch [:table.content-variants/trigger-render-variants %])
           menu-items-action-mapping {:publish {:label "Publish"
                                                :icon "publish"
                                                :order 25
                                                :on-click (partial update-status :publish)}
                                      :unpublish {:label "Unpublish"
                                                  :icon "file_upload"
                                                  :iconProps {:sx #js {:transform "rotate(180deg)"}}
                                                  :order 30
                                                  :on-click (partial update-status :unpublish)}
                                      :approve {:label "Approve"
                                                :icon "check"
                                                :order 10
                                                :on-click (partial update-status :approve)}
                                      :approve-publish {:label "Approve & Publish"
                                                        :icon "check"
                                                        :order 20
                                                        :on-click (partial update-status :approve-publish)}
                                      :unapprove {:label "Unapprove"
                                                  :icon "close"
                                                  :order 15
                                                  :on-click (partial update-status :unapprove)}
                                      :unpublish-unapprove {:label "Unpublish & Unapprove"
                                                            :icon "file_upload"
                                                            :iconProps {:sx #js {:transform "rotate(180deg)"}}
                                                            :order 5
                                                            :on-click (partial update-status :unpublish-unapprove)}}
           allowed-actions (use-subscribe [:table.content-variants/selected-rows-allowed-actions])
           render-actions (use-subscribe [:table.content-variants/selected-rows-allowed-render-actions])
           menu-items-action-rendering (fn [v]
                                         (if (not= "display" channel)
                                           (case v
                                             (:created
                                              :failed
                                              :unknown) [{:label "Start Renders"
                                                          :icon "label"
                                                          :order 1
                                                          :on-click (partial trigger-renders :trigger)}]

                                             (:rendering
                                              :render-starting
                                              :queued) [{:label "Cancel Renders"
                                                         :icon "close"
                                                         :order 2
                                                         :on-click (partial trigger-renders :cancel)}]
                                             :succeeded []
                                             [])
                                           []))
           menu-items (->> (map menu-items-action-mapping allowed-actions)
                           (concat (mapcat menu-items-action-rendering render-actions))
                           (sort-by :order))

           available-bulk-actions (use-subscribe [:table.content-variants/bulk-actions])]
       (uix/use-effect (fn []
                         (localstorage/save! (keyword (str "content-variant-filters-" adset-id)) filter-model))
                       [adset-id filter-model])
       ($ dg/grid-toolbar
          {:hide-quick-filter true
           :sx #js {:px 2}}
          ($ Stack
             {:direction "row"
              :gap 1
              :alignItems "center"}
             ($ GridToolbarColumnsButton)
             ($ GridToolbarDensitySelector)
             ($ GridToolbarFilterButton)
             ($ content-columns-toggle
                {:show-content-columns show-content-columns
                 :set-show-content-columns set-show-content-columns}))

          (if (seq menu-items)
            ($ Stack {:direction "row"}
               (for [[index {:keys [label on-click iconProps] icon-name :icon}] (mc/seq-indexed menu-items)]
                 ($ Button
                    {:key index
                     :variant "text"
                     :size "small"
                     :startIcon ($ Icon iconProps icon-name)
                     :onClick on-click
                     :data-testid label}
                    label)))
            ($ bulk-actions {:actions available-bulk-actions})))))))

(defui variants-grid []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])

        {rows :value state :state} (use-subscribe [:table.content-variants/value])
        total-row-count (use-subscribe [:table.content-variants/total-variant-count])
        {page-size :size} (use-subscribe [:table.content-variants/page])

        initial-filter-model (localstorage/get (keyword (str "content-variant-filters-" adset-id)))

        [show-content-columns set-show-content-columns] (uix/use-state false)
        [selection-model set-selection-model] (uix/use-state #js [])
        [pagination-model set-pagination-model] (uix/use-state #js {:page 0 :pageSize page-size})

        page->cursor (uix/use-ref {})

        has-next-page? (use-subscribe [:table.content-variants/has-next-page? (:page pagination-model)])

        columns (concat (row-number-column)
                        (identifier-columns)
                        (when show-content-columns
                          (content-columns))
                        (template-dimensions-columns)
                        (targeting-columns)
                        (optimizing-columns)
                        (click-through-columns)
                        (impression-tracker-columns)
                        (click-tracker-columns)
                        (censor-columns)
                        (last-modified-column)
                        (use-status-columns)
                        (action-column))

        debounce (uix/use-memo (fn []
                                 (debouncer/make-debouncer 500))
                               [])

        throttle (uix/use-memo (fn []
                                 (debouncer/make-throttler 800))
                               [])

        initial-state (uix/use-memo (fn []
                                      (clj->js
                                       (cond-> {:pinnedColumns {:left [(.-field GRID_CHECKBOX_SELECTION_COL_DEF)]
                                                                :right ["updated-at" "renders" "status" "score" "actions"]}}
                                         initial-filter-model (assoc :filter {:filterModel initial-filter-model}))))
                                    [initial-filter-model])]

    (uix/use-effect (fn []
                      (when initial-filter-model
                        (table.db/on-filter-model-changed columns initial-filter-model nil)))
                    [columns initial-filter-model])

    (uix/use-effect (fn []
                      (rf/dispatch [:table.content-variants/set-cursor-and-boundary
                                    :starting-after (get @page->cursor (dec (.-page pagination-model)))]))
                    [pagination-model])

    (uix/use-effect (fn []
                      (when (and (not= state :pending) (seq rows))
                        (let [starting-after-cursor (:cursor (last rows))]
                          (swap! page->cursor assoc (.-page pagination-model) starting-after-cursor))))
                    [pagination-model state rows])

    ($ DataGridPremium
       {:rows (to-array rows)
        :rowCount total-row-count
        :getRowId (fn [^js row] (:id row))

        :columns (clj->js columns)
        :columnHeaderHeight 70

        :loading (= state :pending)

        :keepNonExistentRowsSelected true
        :pagination true
        :paginationMode "server"
        :paginationMeta {:hasNextPage has-next-page?}
        :paginationModel pagination-model
        :onPaginationModelChange (throttle (fn [model]
                                             (let [page (.-page model)
                                                   cursor (get @page->cursor (dec page))]
                                               (when (or (= page 0) cursor)
                                                 (set-pagination-model model)))))
        :pageSizeOptions #js [(or page-size 100)]

        :filterMode "server"
        :sortingMode "server"
        :onFilterModelChange (debounce (fn [& args]
                                         (set-pagination-model #js {:page 0 :pageSize page-size})
                                         (reset! page->cursor {})
                                         (apply table.db/on-filter-model-changed args)) columns)
        :onSortModelChange (fn [[sort-props]]
                             (let [{:keys [field sort]} (js->clj sort-props :keywordize-keys true)]
                               (table.db/on-sorting-model-changed columns field sort)))

        :initialState initial-state

        :checkboxSelection true
        :rowSelectionModel selection-model
        :onRowSelectionModelChange (fn [^js selection]
                                     (let [selection-limit 1000
                                           selection-count (. selection -length)
                                           sliced-selection (.. selection (slice 0 selection-limit))]
                                       (when (> selection-count selection-limit)
                                         (rf/dispatch [:modal.content-variants/open-selection-limit-modal selection-limit]))

                                       (set-selection-model sliced-selection)
                                       (rf/dispatch [:table.content-variants/select-rows (mapv (comp parse-long str) sliced-selection)])))

        :sx (fn [theme]
              #js {:borderTopWidth 0
                   ".MuiDataGrid-columnHeaders" #js {:borderTopStyle "solid"
                                                     :borderColor (mui/get-table-border-color theme)
                                                     :borderTopWidth "1px"
                                                     :borderRadius 0}
                   ".MuiDataGrid-columnHeaderTitleContainerContent" #js {:height "34px"
                                                                         :alignItems "start"}
                   ".MuiDataGrid-columnHeaderTitle" #js {:lineHeight 1
                                                         :color "grey.100"}})
        :slots #js {:toolbar Toolbar
                    :columnMenu ColumnMenu}
        :slotProps #js {:toolbar #js {:show-content-columns show-content-columns
                                      :set-show-content-columns set-show-content-columns}}})))
